// utils/appAccess.js
//
// App Access (cohorts, grants, join): enums, the fs-auth wire seam, date
// sentinels, provenance filters, and the Approval Queue's pure logic.
// Ported from FireSyncPortal.Models (AccessCohort.cs / AccessGrant.cs /
// JoinModels.cs) and the display/grouping helpers in the legacy
// ApprovalQueue and SpecialAppAccess pages. Full behavior spec:
// "FireSync Portal Development/APP-ACCESS-JOIN-RETROFIT.md".
//
// The enums serialize as ints and are APPEND-ONLY server-side — keep these
// lists in the server's order and reference the names, never the numbers.

import { apiPost } from '@/utils/apiHelpers';

export const FS_AUTH_ACCESS_API = '/api/fsauth/namedaccess';

/** POST to fs-auth's namedaccess/<endpoint>. Same contract as apiPost:
 * never throws, callers branch on `ok`. Key + OrgId ride in the body —
 * no auth headers (wire idiom, retrofit doc §2). */
export function accessPost(endpoint, body, context = {}) {
    return apiPost(`${FS_AUTH_ACCESS_API}/${endpoint}`, body,
        { action: `appAccess:${endpoint}`, ...context });
}

// ── Enums (FireSyncPortal.Models, int-serialized, append-only) ──

export const MEMBERSHIP_KIND = { Standard: 0, Temporary: 1 };
export const JOINED_VIA = { Unset: 0, Admin: 1, Csv: 2, Invite: 3, Code: 4 };
export const COHORT_KIND = { Special: 0, RegularRoster: 1 };
export const APPROVAL_POLICY = { Manual: 0, Auto: 1 };
export const COHORT_STATUS = { Active: 0, Closed: 1 };
export const GRANT_DIRECTION = { Invite: 0, Request: 1, Code: 2 };
export const GRANT_STATUS = { Pending: 0, Approved: 1, Declined: 2, Expired: 3, Revoked: 4 };
export const SUBMITTED_VIA = { Web: 0, App: 1 };
export const VERIFIED_VIA = { Unset: 0, Pin: 1, Inherited: 2 };

export const JOIN_RESULT_CODES = {
    None: 0, Joined: 1, Pending: 2, NeedsContactCheck: 3, UnknownCode: 4,
    ExpiredCode: 5, GroupClosed: 6, InviteContactMismatch: 7, AlreadyMember: 8,
    AlreadyMemberUnverifiedDevice: 9, RequestPending: 10, MemberSuspended: 11,
    OrgCapReached: 12, RateLimited: 13,
};

// ── Date sentinels + parsing ──
//
// "Never/none" is DateTime.MinValue, serialized as year 0001. The Blazor
// pages test `date.Year >= 2000`; replicate that exactly — anything before
// 2000 renders as "Never"/"—" and is never treated as a real date.

/**
 * A wire DateTime as a JS Date, treating a naive ISO string as UTC.
 *
 * Newtonsoft serializes Unspecified-kind DateTimes WITHOUT a timezone
 * suffix, and the Blazor pages then call ToLocalTime() on them — i.e. the
 * value IS UTC even when the string doesn't say so. `new Date()` on a naive
 * string assumes LOCAL, which would shift every timestamp by the viewer's
 * offset — so append Z unless an offset is already present.
 */
export function parseWireDate(value) {
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (!value || typeof value !== 'string') return null;
    const hasZone = /[zZ]$|[+-]\d\d:\d\d$/.test(value);
    const d = new Date(hasZone ? value : `${value}Z`);
    return Number.isNaN(d.getTime()) ? null : d;
}

/** True when the value is the "never/none" sentinel (year < 2000). */
export function isNeverDate(value) {
    const d = parseWireDate(value);
    return !d || d.getUTCFullYear() < 2000;
}

// Expiration and Joined format the wire value RAW — the legacy pages call
// plain ToString with no ToLocalTime (only FormatRequested localizes).
// These are calendar dates; converting them to the viewer's zone would
// shift "expires 10/15" to 10/14 for anyone east of UTC.

/**
 * Verified-contact markers (fs-auth 2026-09-08): NamedAccessMember.MobileVerifiedDate /
 * EmailVerifiedDate are the UTC moment the contact was last proven by a code (or
 * inherited at grant approval); year-0001 = never, or cleared by a contact edit.
 * Vocabulary is the retrofit's "contact confirmed" (rule 3): reachability, not identity.
 */
export function isContactConfirmed(value) {
    return !isNeverDate(value);
}

/** Tooltip for the mark: "Contact confirmed MM/DD/YY". Null when not confirmed. */
export function contactConfirmedTitle(value) {
    return isContactConfirmed(value) ? `Contact confirmed ${formatJoinedDate(value)}` : null;
}

/** Expiration cells: "MM/DD/YYYY" or "Never". */
export function formatExpiration(value) {
    const d = parseWireDate(value);
    if (!d || d.getUTCFullYear() < 2000) return 'Never';
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${mm}/${dd}/${d.getUTCFullYear()}`;
}

/** Joined cells: "MM/DD/YY" or "—". */
export function formatJoinedDate(value) {
    const d = parseWireDate(value);
    if (!d || d.getUTCFullYear() < 2000) return '—';
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${mm}/${dd}/${String(d.getUTCFullYear()).slice(-2)}`;
}

/**
 * The Approval Queue's "Requested" column: relative for the last two days,
 * short date after ("today HH:mm" / "yesterday HH:mm" / "MMM d, HH:mm",
 * viewer-local). `now` is injectable for tests.
 */
export function formatRequested(value, now = new Date()) {
    const d = parseWireDate(value);
    if (!d || d.getUTCFullYear() < 2000) return '—';

    const hhmm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffDays = Math.round((today - day) / 86400000);

    if (diffDays === 0) return `today ${hhmm}`;
    if (diffDays === 1) return `yesterday ${hhmm}`;
    return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${hhmm}`;
}

/** 10 digits pretty-printed, anything else unchanged (queue Contact column —
 * deliberately NOT the roster's formatPhone, which also strips a leading 1). */
export function formatMobile(mobile) {
    const digits = String(mobile || '').replace(/\D/g, '');
    if (digits.length !== 10) return mobile;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

// ── Provenance (Joined Via / Joined) ──

/**
 * Display label for a joinedVia value. The Code label differs by surface:
 * the org roster says "Self-Reg", a group's member table says "QR / Code" —
 * pass `codeLabel` accordingly. Unset renders as "—" everywhere.
 */
export function joinedViaLabel(via, codeLabel = 'QR / Code') {
    switch (via) {
        case JOINED_VIA.Admin: return 'Admin';
        case JOINED_VIA.Csv: return 'CSV';
        case JOINED_VIA.Invite: return 'Invite';
        case JOINED_VIA.Code: return codeLabel;
        default: return '—';
    }
}

const RANGE_HOURS = { '24h': 24, '7d': 24 * 7, '30d': 24 * 30 };

/**
 * The Joined-via + Joined-window filters, composable with a page's other
 * filters. `joinedVia`: 'all' | 'admin' | 'csv' | 'invite' | 'code' —
 * **'admin' also matches Unset**, because pre-feature roster rows predate
 * provenance and were all admin-entered (retrofit doc §9.5).
 * `joinedRange`: 'any' | '24h' | '7d' | '30d', UTC cutoffs from `now`.
 */
export function filterMembersByProvenance(members, { joinedVia = 'all', joinedRange = 'any' } = {}, now = new Date()) {
    let result = members || [];

    switch (joinedVia) {
        case 'invite': result = result.filter(m => m.joinedVia === JOINED_VIA.Invite); break;
        case 'code': result = result.filter(m => m.joinedVia === JOINED_VIA.Code); break;
        case 'csv': result = result.filter(m => m.joinedVia === JOINED_VIA.Csv); break;
        case 'admin':
            result = result.filter(m =>
                m.joinedVia === JOINED_VIA.Admin || m.joinedVia === JOINED_VIA.Unset
                || m.joinedVia == null);
            break;
        default: break;
    }

    const hours = RANGE_HOURS[joinedRange];
    if (hours) {
        const cutoff = now.getTime() - hours * 3600000;
        result = result.filter(m => {
            const d = parseWireDate(m.joinedDate);
            return d != null && d.getTime() >= cutoff;
        });
    }

    return result;
}

// ── Group CSV preview (pure port of the SpecialAppAccess dedupe) ──
//
// The preview is a courtesy — the server's import stays authoritative — but
// its keys must mirror the server's matching rules exactly or the counts lie.

/** The server's email key: lowercase + trim; null when blank. */
export function normalizeEmailKey(email) {
    if (!email || !String(email).trim()) return null;
    return String(email).trim().toLowerCase();
}

/** The server's mobile key: digits only, LAST 10; null when no digits. */
export function normalizeMobileKey(mobile) {
    if (!mobile || !String(mobile).trim()) return null;
    let digits = String(mobile).replace(/\D/g, '');
    if (digits.length === 0) return null;
    if (digits.length > 10) digits = digits.slice(-10);
    return digits;
}

/**
 * Classify CSV entries against the FULL org roster:
 * New / Update (matched by email or mobile) / Skipped (no contact, or
 * repeated in the file). Both keys register in the in-file dedupe even when
 * the first already matched — legacy uses a deliberate non-short-circuit OR
 * so a row can't sneak in later under its other key.
 *
 * @param {Array<{email?, mobile?}>} entries parsed CSV rows
 * @param {Array<{email?, mobile?}>} roster  the whole org roster
 * @returns {Array<{entry, kind: 'New'|'Update'|'Skipped', note: string}>}
 */
export function classifyCsvEntries(entries, roster) {
    const byEmail = new Set();
    const byMobile = new Set();
    for (const existing of roster || []) {
        const ek = normalizeEmailKey(existing.email);
        if (ek != null) byEmail.add(ek);
        const mk = normalizeMobileKey(existing.mobile);
        if (mk != null) byMobile.add(mk);
    }

    const seenInFile = new Set();
    const addOnce = key => {
        if (seenInFile.has(key)) return false;
        seenInFile.add(key);
        return true;
    };

    return (entries || []).map(entry => {
        const emailKey = normalizeEmailKey(entry.email);
        const mobileKey = normalizeMobileKey(entry.mobile);

        if (emailKey == null && mobileKey == null) {
            return { entry, kind: 'Skipped', note: 'no email or mobile' };
        }

        // Evaluate BOTH adds before deciding (the legacy `|`).
        const emailRepeat = emailKey != null && !addOnce(`e:${emailKey}`);
        const mobileRepeat = mobileKey != null && !addOnce(`m:${mobileKey}`);
        if (emailRepeat || mobileRepeat) {
            return { entry, kind: 'Skipped', note: 'repeated in file' };
        }

        if (emailKey != null && byEmail.has(emailKey)) {
            return { entry, kind: 'Update', note: 'matched by email' };
        }
        if (mobileKey != null && byMobile.has(mobileKey)) {
            return { entry, kind: 'Update', note: 'matched by mobile' };
        }
        return { entry, kind: 'New', note: '' };
    });
}

// ── Approval Queue grouping (pure port of ApprovalQueue.razor.cs) ──

/**
 * Pending grants → display groups. Grouped by cohort; the RegularRoster
 * cohort is titled "Org App Access — Self-Registration" and ordered LAST;
 * others by title; rows oldest-first within a group. The applied-defaults
 * note derives from the first grant.
 */
export function groupGrants(grants) {
    const byCohort = new Map();
    for (const g of grants || []) {
        const key = g.cohortId || '';
        if (!byCohort.has(key)) byCohort.set(key, []);
        byCohort.get(key).push(g);
    }

    const groups = [...byCohort.values()].map(list => {
        const first = list[0];
        const isRegularRoster = first.cohortKind === COHORT_KIND.RegularRoster;

        let applied;
        if (first.membershipKind === MEMBERSHIP_KIND.Temporary) {
            applied = isNeverDate(first.expirationRequested)
                ? 'temporary member, no expiration'
                : `expires ${parseWireDate(first.expirationRequested)
                    .toLocaleDateString(undefined,
                        { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}`;
        } else {
            applied = 'standard member, no expiration';
        }

        return {
            // The grouping key — two cohorts may share a NAME, never an id.
            cohortId: first.cohortId || '',
            title: isRegularRoster
                ? 'Org App Access — Self-Registration'
                : (first.cohortName || 'Direct Requests'),
            isRegularRoster,
            appliedNote: `On approve: ${applied}`,
            grants: [...list].sort((a, b) => {
                const da = parseWireDate(a.createdUtc)?.getTime() ?? 0;
                const db = parseWireDate(b.createdUtc)?.getTime() ?? 0;
                return da - db;
            }),
        };
    });

    return groups.sort((a, b) =>
        (a.isRegularRoster - b.isRegularRoster)
        || String(a.title).localeCompare(String(b.title), undefined, { sensitivity: 'base' }));
}
